import { Component, Input, Output, EventEmitter, Type, ComponentRef, ViewContainerRef, ViewChild, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, signal, effect, QueryList, ViewChildren, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { IconComponent, IconSize } from '../icon/icon.component';

export type TabIconSource = 'primeng' | 'lucide' | 'svg';
export type TabIconSize = IconSize | 'sm' | 'default' | 'lg' | 'xl';

export interface ITabPanelConfig {
    value: string;
    label: string;
    icon?: string;
    iconSource?: TabIconSource;
    iconSize?: TabIconSize;
    tour?: {
        stepId: string;
    };
    badge?: string | number;
    badgeClass?: string;
    avatar?: string;
    component?: Type<any>;
    visible?: boolean;
    disabled?: boolean;
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    customClass?: string;
    data?: any; // Additional data to pass to the component
}

@Component({
    selector: 'app-tabs-panels',
    standalone: true,
    imports: [CommonModule, TabsModule, AvatarModule, BadgeModule, TooltipModule, IconComponent],
    templateUrl: './tabs-panels.component.html',
    styleUrl: './tabs-panels.component.scss'
})
export class TabsPanelsComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() tabs: ITabPanelConfig[] = [];
    @Input() set activeTabValue(value: string) {
        if (value !== undefined && value !== null) {
            this.activeTab.set(value);
        }
    }

    @Input() scrollable: boolean = true;
    @Input() showPrevIcon: boolean = false;
    @Input() showNextIcon: boolean = false;
    @Input() prevIconClass: string = 'pi pi-chevron-left';
    @Input() nextIconClass: string = 'pi pi-chevron-right';
    @Input() customClass: string = '';
    @Input() tabListClass: string = '';
    @Input() tabPanelsClass: string = '';
    @Input() variant: 'default' | 'modern' = 'default';
    @Input() panelBorder: 'theme' | 'default' | 'none' = 'none';
    /** Optional template rendered flush-right inside the tab bar (e.g. a Setup button). */
    @Input() rightActions?: TemplateRef<any>;

    @Output() tabChanged = new EventEmitter<{ value: string, tab: ITabPanelConfig }>();

    @ViewChildren('tabContentContainer', { read: ViewContainerRef }) tabContentContainers!: QueryList<ViewContainerRef>;

    activeTab = signal<string>('0');
    private componentRefs = new Map<string, ComponentRef<any>>();
    private containersReady = false;

    constructor() {
        // Effect to render component when active tab changes
        effect(() => {
            const currentTab = this.activeTab();
            if (this.containersReady) {
                this.renderActiveTabComponent(currentTab);
            }
        });
    }

    get visibleTabs(): ITabPanelConfig[] {
        return this.tabs.filter(tab => tab.visible !== false);
    }

    ngAfterViewInit(): void {
        // Mark containers as ready
        setTimeout(() => {
            this.containersReady = true;

            // Set initial tab if not set (though setter should catch it if bound)
            // If activeTab is still '0' (default) and that's not valid, try to fix it
            const current = this.activeTab();
            if (this.visibleTabs.length > 0 && !this.visibleTabs.some(t => t.value === current)) {
                this.activeTab.set(this.visibleTabs[0].value);
            }

            // Create all components
            this.createAllTabComponents();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabs']) {
            // If tabs change, ensure active tab is still valid
            const currentTab = this.activeTab();
            const isStillValid = this.visibleTabs.some(t => t.value === currentTab);

            if (!isStillValid && this.visibleTabs.length > 0) {
                this.activeTab.set(this.visibleTabs[0].value);
            }

            // If containers are ready and tabs have changed, recreate components
            if (this.containersReady) {
                setTimeout(() => {
                    this.createAllTabComponents();
                });
            }
        }
    }

    onTabChange(event: any): void {
        const newValue = event !== undefined ? event.toString() : '0';
        this.activeTab.set(newValue);

        const tab = this.tabs.find(t => t.value === newValue);
        if (tab) {
            this.tabChanged.emit({ value: newValue, tab });
        }
    }

    private createAllTabComponents(): void {
        if (!this.tabContentContainers || this.tabContentContainers.length === 0) {
            return;
        }

        const containers = this.tabContentContainers.toArray();

        // Wait for containers to match visible tabs count
        if (containers.length !== this.visibleTabs.length) {
            // Retry after change detection
            setTimeout(() => this.createAllTabComponents(), 50);
            return;
        }

        this.visibleTabs.forEach((tab, index) => {
            if (tab.component && !this.componentRefs.has(tab.value)) {
                const container = containers[index];
                if (container) {
                    const componentRef = container.createComponent(tab.component);

                    // Pass any data to the component if provided
                    if (tab.data && componentRef.instance) {
                        Object.assign(componentRef.instance, tab.data);
                    }

                    this.componentRefs.set(tab.value, componentRef);
                }
            }
        });
    }

    private renderActiveTabComponent(tabValue: string): void {
        // Components are already created, just ensure they're visible
        // PrimeNG tabs handles the visibility automatically
    }

    setActiveTab(value: string): void {
        const tab = this.visibleTabs.find(t => t.value === value);
        if (tab && !tab.disabled) {
            this.activeTab.set(value);
        }
    }

    getActiveTab(): string {
        return this.activeTab();
    }

    ngOnDestroy(): void {
        // Clean up component references
        this.componentRefs.forEach(ref => ref.destroy());
        this.componentRefs.clear();
    }
}

